import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Tag, Modal, Button, Image, Tooltip, message } from 'antd';
import { DownOutlined, UpOutlined, ExpandOutlined } from '@ant-design/icons';
import { TextVisitCase, TextVisitServiceType, IPaymentStatus } from 'types/textVisit';
import dayjs from 'utils/dayjs';
import s from './s.module.less';
import { capitalize } from 'lodash';
import ProsepectInfo from 'pages/dashboard/ProspectsDetailPage/components/prosepectInfo';
import MaskedFrontImage from '../../../imgs/masked-front-img.png';
import MaskedBackImage from '../../../imgs/masked-back-img.png';
import { FollowUpServiceTypeMap } from '../MyCaseCard';
import { providerGetKlarityResourcesUrl } from 'api/provider';
import { getPrivateFilePreview } from 'api/operation';
import { getIntakeFormUrlByTextVisitId, isOpPortal } from 'utils/common';
import PatientHealthInfoUpdateDialog, { PatientUpdateType } from 'components/PatientUpdate/HealthInfoDialog';
import PencilIcon from 'assets/common/pencil.svg';
import classNames from 'classnames';
import IDUpdateDialog from 'components/PatientUpdate/IDUpdateDialog';
import { TPharmacy } from 'types/common';
import { EIntakeFormStatus } from 'types/calendar';
import { sendMessage } from 'api/inbox';
import { getUserId } from 'utils/localstore';

interface PreviewDetailProps {
    isAcceptMode?: boolean;
    showHeader?: boolean;
    onChat?: () => void;
    caseData: TextVisitCase;
    isProviderEligible?: boolean;
    shouldMaskData?: boolean; // If true, mask sensitive data (for all cases tab)
    refetchCaseData?: () => void;
    showBillingForVisit?: boolean;
}

const initPatientUpdateDialog = {
    open: false,
    type: PatientUpdateType.CurrentMedications,
};

const PreviewDetail: React.FC<PreviewDetailProps> = ({
    caseData,
    isAcceptMode = false,
    showHeader = true,
    onChat,
    isProviderEligible,
    shouldMaskData = false,
    refetchCaseData,
    showBillingForVisit,
}) => {
    const [intakeExpanded, setIntakeExpanded] = useState(true);
    const [imageModalVisible, setImageModalVisible] = useState(false);
    const [currentImage, setCurrentImage] = useState<string>('');
    const [imageUrlMap, setImageUrlMap] = useState<Record<string, string>>({});
    const [, contextHolder] = Modal.useModal();
    const [patientUpdateDialog, setPatientUpdateDialog] = useState({ ...initPatientUpdateDialog });
    const [idUpdateDialogOpen, setIdUpdateDialogOpen] = useState(false);
    const [sendingIntakeReminder, setSendingIntakeReminder] = useState(false);
    console.log({
        caseData,
    });
    const isFollowUpCase = caseData.serviceType !== TextVisitServiceType.INITIAL_VISIT;
    const caseTitle = isFollowUpCase
        ? FollowUpServiceTypeMap[caseData.serviceType as keyof typeof FollowUpServiceTypeMap]?.title
        : caseData.assessmentName;
    const intakeFormCompleted = caseData?.intakeFormStatus === EIntakeFormStatus.COMPLETED;

    const resourceKeys = useMemo(() => {
        const keys: string[] = [];
        if (caseData.intakeQuestions) {
            caseData.intakeQuestions.forEach((question) => {
                if (!question.answer) return;
                try {
                    const answer = JSON.parse(question.answer);
                    const answerArray = Array.isArray(answer) ? answer : [answer];
                    answerArray.forEach((ans: unknown) => {
                        if (typeof ans === 'object' && ans !== null) {
                            const answerObj = ans as {
                                option?: string;
                                images?: string[];
                                imageContentUri?: string;
                            };
                            if (answerObj?.images && Array.isArray(answerObj.images)) {
                                answerObj.images.forEach((key) => {
                                    if (key && !keys.includes(key)) {
                                        keys.push(key);
                                    }
                                });
                            }
                        }
                    });
                } catch {
                    // ignore parse error
                }
            });
        }
        return keys;
    }, [caseData.intakeQuestions]);

    useEffect(() => {
        const fetchImageUrls = async () => {
            if (resourceKeys.length === 0) return;

            const urlMap: Record<string, string> = {};
            const isOps = isOpPortal();
            await Promise.all(
                resourceKeys.map(async (resourceKey) => {
                    try {
                        const res = isOps
                            ? await getPrivateFilePreview(resourceKey)
                            : await providerGetKlarityResourcesUrl(resourceKey);
                        if (!res.error && res.data?.data?.singedUrl) {
                            urlMap[resourceKey] = res.data.data.singedUrl;
                        }
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(`Failed to fetch image URL for resourceKey: ${resourceKey}`, error);
                    }
                }),
            );
            setImageUrlMap(urlMap);
        };

        fetchImageUrls();
    }, [resourceKeys]);

    const handleImageClick = (imageUrl: string) => {
        setCurrentImage(imageUrl);
        setImageModalVisible(true);
    };

    const handleOpenPatientUpdateDialog = useCallback((type: PatientUpdateType) => {
        return () => {
            setPatientUpdateDialog({
                type,
                open: true,
            });
        };
    }, []);

    const getSensitiveType = (questionName: string): 'name' | 'date' | 'phone' | 'address' | 'pharmacy' | null => {
        if (!questionName) return null;
        const lower = questionName.toLowerCase();
        if (lower.includes('name')) return 'name';
        if (
            lower.includes('birthday') ||
            lower.includes('date of birth') ||
            lower.includes('dob') ||
            lower.includes('birth date')
        )
            return 'date';
        if (
            lower.includes('phone') ||
            lower.includes('telephone') ||
            lower.includes('contact number') ||
            lower.includes('mobile')
        )
            return 'phone';
        if (lower.includes('pharmacy')) return 'pharmacy';
        if (lower.includes('address') || lower.includes('street')) return 'address';
        return null;
    };

    const maskAnswerText = (
        text: string,
        sensitiveType: 'name' | 'date' | 'phone' | 'address' | 'pharmacy' | null,
    ): string => {
        if (!text || !sensitiveType) return text || '*****';

        if (sensitiveType === 'name') {
            const words = text.trim().split(/\s+/);
            if (words.length >= 2) {
                return `${words[0]}. ${words[words.length - 1].charAt(0).toUpperCase()}`;
            }
            return words[0] || '*****';
        }

        if (sensitiveType === 'date') {
            const ageMatch = text.match(/(\d+)\s*(?:year|yr|years?)\s*(?:old|-old)/i);
            return ageMatch ? `**/**/**** (${ageMatch[1]} year-old)` : '**/**/****';
        }

        if (sensitiveType === 'phone') {
            return '(***)-***-****';
        }

        return '*****';
    };

    const renderAnswerItem = (
        ans: unknown,
        idx: number,
        showBullet = true,
        shouldMask = false,
        sensitiveType: 'name' | 'date' | 'phone' | 'address' | 'pharmacy' | null = null,
    ) => {
        if (typeof ans === 'string') {
            const displayText = shouldMask ? maskAnswerText(ans, sensitiveType) : ans;
            if (!showBullet) {
                return displayText;
            }
            return (
                <div key={idx} className={s.motivationItem}>
                    • {displayText}
                </div>
            );
        }

        if (typeof ans === 'object' && ans !== null) {
            const answerObj = ans as {
                option?: string;
                images?: string[];
                imageContentUri?: string;
            };

            if (shouldMask) {
                if (answerObj?.option) {
                    const maskedOption = maskAnswerText(answerObj.option, sensitiveType);
                    return (
                        <div key={idx} className={s.motivationItem}>
                            {showBullet && '• '}
                            {maskedOption}
                        </div>
                    );
                }
                return (
                    <div key={idx} className={s.motivationItem}>
                        {showBullet && '• '}
                        *****
                    </div>
                );
            }

            let images: string[] = [];
            if (answerObj?.images && Array.isArray(answerObj.images)) {
                images = answerObj.images
                    .map((resourceKey) => imageUrlMap[resourceKey] || resourceKey)
                    .filter((uri) => !!uri);
            } else if (answerObj?.imageContentUri) {
                images = [answerObj.imageContentUri];
            }

            if (answerObj?.option && images.length > 0) {
                const shouldShowOption = answerObj.option !== 'CUSTOM';
                return (
                    <div key={idx} className={s.motivationItem}>
                        {showBullet && '• '}
                        <div className={s.motivationImageContainer}>
                            <Image.PreviewGroup
                                // @ts-ignore
                                items={images}
                            >
                                {images
                                    .filter((uri) => !!uri)
                                    .map((uri) => (
                                        <div key={uri} className={s.motivationImageWrapper}>
                                            <div style={{ position: 'relative' }}>
                                                <Image
                                                    className={s.motivationImage}
                                                    src={uri}
                                                    preview={{
                                                        src: uri,
                                                        mask: false,
                                                    }}
                                                />
                                                <div
                                                    className={s.expandIcon}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const imageElement =
                                                            e.currentTarget.parentElement?.querySelector('img');
                                                        if (imageElement) {
                                                            imageElement.click();
                                                        }
                                                    }}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="14"
                                                        height="14"
                                                        viewBox="0 0 14 14"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M2.33203 4.66665V2.33331M2.33203 2.33331H4.66536M2.33203 2.33331L5.2487 5.24998M11.6654 4.66665V2.33331M11.6654 2.33331H9.33203M11.6654 2.33331L8.7487 5.24998M2.33203 9.33331V11.6666M2.33203 11.6666H4.66536M2.33203 11.6666L5.2487 8.74998M11.6654 11.6666L8.7487 8.74998M11.6654 11.6666V9.33331M11.6654 11.6666H9.33203"
                                                            stroke="white"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                            {shouldShowOption && (
                                                <div className={s.motivationImageOption}>{answerObj.option}</div>
                                            )}
                                        </div>
                                    ))}
                            </Image.PreviewGroup>
                        </div>
                    </div>
                );
            }
        }

        const displayValue = shouldMask ? maskAnswerText(String(ans), sensitiveType) : String(ans);
        if (!showBullet) {
            return displayValue;
        }
        return (
            <div key={idx} className={s.motivationItem}>
                • {displayValue}
            </div>
        );
    };

    const renderAnswerContent = (
        answer: unknown,
        showBullet = true,
        shouldMask = false,
        sensitiveType: 'name' | 'date' | 'phone' | 'address' | 'pharmacy' | null = null,
    ): React.ReactNode => {
        if (!answer) return '-';

        let parsedAnswer = answer;
        if (typeof answer === 'string') {
            try {
                parsedAnswer = JSON.parse(answer);
            } catch {
                return shouldMask ? maskAnswerText(answer, sensitiveType) : answer;
            }
        }

        if (!Array.isArray(parsedAnswer)) {
            parsedAnswer = [parsedAnswer];
        }

        const filteredAnswer = (parsedAnswer as unknown[]).filter((a: unknown) => a != null);

        if (filteredAnswer.length === 0) return '-';

        const hasImages = filteredAnswer.some((item) => {
            if (typeof item === 'object' && item !== null) {
                const obj = item as { images?: string[] };
                return Array.isArray(obj.images) && obj.images.length > 0;
            }
            return false;
        });

        if (filteredAnswer.length === 1 && typeof filteredAnswer[0] === 'string' && !hasImages) {
            return shouldMask ? maskAnswerText(filteredAnswer[0], sensitiveType) : filteredAnswer[0];
        }

        return (
            <>
                {filteredAnswer.map((ans: unknown, idx: number) =>
                    renderAnswerItem(ans, idx, showBullet, shouldMask, sensitiveType),
                )}
            </>
        );
    };

    const getIntakeAnswer = (questionName: string): string => {
        const question = caseData.intakeQuestions?.find((q) =>
            q.fullQuestionName?.toLowerCase().includes(questionName.toLowerCase()),
        );
        if (!question || !question.answer) return '';
        try {
            const answer = JSON.parse(question.answer);
            if (Array.isArray(answer) && answer.length > 0) {
                return answer.join(', ');
            }
            return answer || '';
        } catch {
            return question.answer || '';
        }
    };

    const getIntakeAnswerWithImages = (questionName: string): React.ReactNode => {
        const question = caseData.intakeQuestions?.find((q) =>
            q.fullQuestionName?.toLowerCase().includes(questionName.toLowerCase()),
        );
        if (!question || !question.answer) return '-';
        const fullQuestionName = question.shortQuestionName || question.fullQuestionName || '';
        const sensitiveType = shouldMaskData ? getSensitiveType(fullQuestionName) : null;
        return renderAnswerContent(question.answer, false, !!sensitiveType, sensitiveType);
    };

    const formatWeight = (weightStr: string): string => {
        if (!weightStr) return '';
        const cleaned = weightStr
            .trim()
            .replace(/\s*lbs?\s*$/i, '')
            .trim();
        if (!cleaned) return '';
        return `${cleaned} lbs`;
    };

    const formatHeight = (heightStr: string): string => {
        if (!heightStr) return '';
        const cleaned = heightStr.trim();
        if (/^\d+\s*['’ʹ′]\s*\d+\s*["”″]?$/.test(cleaned)) {
            return cleaned.endsWith('"') ? cleaned : `${cleaned}"`;
        }
        const feetInchesMatch = cleaned.match(/(\d+)\s*(?:feet|ft|')\s*(\d+)\s*(?:inches|in|")?/i);
        if (feetInchesMatch) {
            return `${feetInchesMatch[1]}'${feetInchesMatch[2]}"`;
        }
        const inchesMatch = cleaned.match(/(\d+)\s*(?:inches|in|")?/i);
        if (inchesMatch) {
            const totalInches = parseInt(inchesMatch[1], 10);
            const feet = Math.floor(totalInches / 12);
            const inches = totalInches % 12;
            return `${feet}'${inches}"`;
        }
        return cleaned;
    };

    const getCurrentWeightAndHeight = (): string => {
        const combinedAnswer = getIntakeAnswer('current weight and height') || getIntakeAnswer('weight and height');

        let weightStr = '';
        let heightStr = '';

        if (combinedAnswer) {
            const weightMatch = combinedAnswer.match(/Current\s+weight\s*:\s*(\d+)/i);
            if (weightMatch) {
                weightStr = weightMatch[1];
            }

            const heightMatch = combinedAnswer.match(
                /Current\s+height\s*:\s*(\d+)\s*(?:feet|ft)\s*-\s*(\d+)\s*(?:inches|in)/i,
            );
            if (heightMatch) {
                heightStr = `${heightMatch[1]} feet ${heightMatch[2]} inches`;
            }
        } else {
            weightStr = getIntakeAnswer('current weight');
            heightStr = getIntakeAnswer('current height');
        }

        const formattedWeight = formatWeight(weightStr);
        const formattedHeight = formatHeight(heightStr);

        if (formattedWeight && formattedHeight) {
            return `${formattedWeight} · ${formattedHeight}`;
        }
        if (formattedWeight) return formattedWeight;
        if (formattedHeight) return formattedHeight;
        return '-';
    };
    const formatMaskedName = (firstName: string | null, lastName: string | null): string => {
        if (!firstName && !lastName) return '-';
        if (!firstName) return lastName ? `${lastName.charAt(0).toUpperCase()}.` : '-';
        if (!lastName) return firstName;
        return `${firstName}. ${lastName.charAt(0).toUpperCase()}`;
    };

    const formatMaskedDateOfBirth = (age: number | null): string => {
        return `**/**/****${age ? ` (${age} year-old)` : ''}`;
    };

    const formatMaskedPhone = (phone: string | null): string => {
        if (!phone) return '-';
        return '(***)-***-****';
    };

    const formatMaskedAddress = (
        address: string | null,
        city: string | null,
        state: string | null,
        zip: string | null,
    ): React.ReactNode => {
        if (!address && !city && !state) return '-';
        return (
            <>
                {address && <div>*****</div>}
                {city && state && (
                    <div>
                        {city}, {state} {zip || ''}
                    </div>
                )}
            </>
        );
    };

    const formatMaskedPharmacy = (pharmacy?: TPharmacy): React.ReactNode => {
        if (!pharmacy) return '-';
        return (
            <>
                {pharmacy.name && <div>*****</div>}
                {pharmacy.address && <div>*****</div>}
                {pharmacy.city && pharmacy.state && (
                    <div>
                        {pharmacy.city}, {pharmacy.state} {pharmacy.zipCode || ''}
                    </div>
                )}
            </>
        );
    };

    const renderHomeAddress = (): React.ReactNode => {
        if (shouldMaskData) {
            return formatMaskedAddress(
                caseData.prospect?.address || null,
                caseData.prospect?.city || null,
                caseData.prospect?.state || null,
                caseData.prospect?.zip || null,
            );
        }
        if (caseData.prospect?.address) {
            return (
                <>
                    <div>{caseData.prospect.address}</div>
                    {caseData.prospect.city && caseData.prospect.state && (
                        <div>
                            {caseData.prospect.city}, {caseData.prospect.state} {caseData.prospect.zip || ''}
                        </div>
                    )}
                </>
            );
        }
        return '-';
    };

    const renderPharmacy = (): React.ReactNode => {
        if (caseData.pharmacy) {
            if (shouldMaskData) {
                return formatMaskedPharmacy(caseData.pharmacy);
            }
            return (
                <>
                    <div>{caseData.pharmacy.name}</div>
                    <div>{caseData.pharmacy.address}</div>
                    <div>
                        {caseData.pharmacy.city}, {caseData.pharmacy.state} {caseData.pharmacy.zipCode}
                    </div>
                </>
            );
        }
        return '-';
    };

    const renderPatientInfo = () => (
        <>
            <div className={s.patientInfoTable}>
                <div className={s.tableRow}>
                    <div className={s.tableCell}>
                        <div className={s.label}>Legal name</div>
                        <div className={s.value}>
                            {shouldMaskData
                                ? formatMaskedName(caseData.patientFirstName, caseData.patientLastName)
                                : `${caseData.patientFirstName || ''} ${caseData.patientLastName || ''}`.trim() || '-'}
                        </div>
                    </div>
                    <div className={s.tableCell}>
                        <div className={s.label}>Date of birth</div>
                        <div className={s.value}>
                            {shouldMaskData
                                ? formatMaskedDateOfBirth(caseData.patientAge)
                                : `${dayjs(caseData.patientBirthday, 'YYYY-MM-DD').format('MM/DD/YYYY')} (${caseData.patientAge} year-old)`}
                        </div>
                    </div>
                    <div className={s.tableCell}>
                        <div className={s.label}>Gender</div>
                        <div className={s.value}>{capitalize(caseData.patientGender || '-')}</div>
                    </div>
                    <div className={s.tableCell}>
                        <div className={s.label}>State of residence</div>
                        <div className={s.value}>{caseData.state || '-'}</div>
                    </div>
                    <div className={s.tableCell}>
                        <div className={s.label}>Phone number</div>
                        <div className={s.value}>
                            {shouldMaskData ? formatMaskedPhone(caseData.prospect?.tel) : caseData.prospect?.tel || '-'}
                        </div>
                    </div>
                    <div className={s.tableCell}>
                        <div className={s.label}>Home address</div>
                        <div className={s.value}>{renderHomeAddress()}</div>
                    </div>
                    <div className={s.tableCell}>
                        <div className={s.label}>Pharmacy</div>
                        <div className={s.value}>{renderPharmacy()}</div>
                    </div>
                    <div className={s.tableCell}>
                        <div className={s.label}>Current weight & height</div>
                        <div className={s.value}>{getCurrentWeightAndHeight()}</div>
                    </div>
                    <div className={s.tableCell}>
                        <div className={s.label}>Weight loss goal</div>
                        <div className={s.value}>{getIntakeAnswerWithImages('weight loss goal') || '-'}</div>
                    </div>
                    <div className={s.tableCell}>
                        <div className={s.label}>Visit goal</div>
                        <div className={s.value}>{getIntakeAnswerWithImages('visit goal') || '-'}</div>
                    </div>
                </div>
            </div>

            {/* AI Summary */}
            <div className={s.section}>
                <div className={s.sectionHeader}>
                    <span className={s.aiIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M10.2816 1.27173C10.1714 1.2859 10.0463 1.32439 9.94381 1.37565C9.6706 1.51228 9.51412 1.70004 9.22624 2.23666C9.13939 2.39854 9.10795 2.45291 9.05266 2.53682C8.91077 2.75212 8.72605 2.9274 8.49834 3.06278C8.45814 3.08669 8.3447 3.14986 8.24625 3.20317C8.04104 3.31429 7.98596 3.34645 7.87522 3.41983C7.73066 3.51561 7.62953 3.60014 7.56041 3.68295C7.45597 3.80807 7.39041 3.93033 7.34422 4.08604C7.31421 4.18725 7.30542 4.2543 7.30542 4.38202C7.30542 4.50974 7.31421 4.57679 7.34422 4.678C7.38545 4.817 7.43508 4.91632 7.52169 5.03313C7.64229 5.19577 7.84367 5.34369 8.22198 5.54749C8.54792 5.72308 8.63019 5.77625 8.77459 5.90464C8.83912 5.96202 8.93085 6.06062 8.98408 6.12983C9.05719 6.2249 9.11601 6.32216 9.24283 6.55762C9.34541 6.74809 9.37651 6.80167 9.44573 6.90726C9.61431 7.16439 9.7417 7.28753 9.94284 7.38781C10.0219 7.42723 10.1193 7.46118 10.2052 7.47934C10.298 7.49893 10.4705 7.50417 10.5689 7.4904C10.7823 7.46054 10.9844 7.36646 11.1413 7.22394C11.2712 7.10595 11.4055 6.9153 11.5612 6.62755C11.7816 6.22032 11.8374 6.13584 11.9892 5.97922C12.1376 5.82609 12.2527 5.74623 12.5783 5.57025C12.8011 5.44985 12.8423 5.42596 12.9556 5.35097C13.1235 5.23995 13.2291 5.14935 13.3083 5.04842C13.5519 4.73776 13.6079 4.3214 13.4542 3.96282C13.373 3.77354 13.2623 3.63575 13.0844 3.50274C12.9484 3.40096 12.8443 3.3375 12.574 3.19146C12.4781 3.13963 12.3697 3.07921 12.3332 3.0572C12.0771 2.90279 11.8749 2.6954 11.7224 2.43042C11.7038 2.39808 11.6539 2.30736 11.6116 2.22882C11.5088 2.03805 11.4719 1.97357 11.4129 1.88117C11.3568 1.79325 11.2672 1.67136 11.2217 1.62122C11.0494 1.43105 10.821 1.30888 10.5731 1.27431C10.5062 1.26497 10.3453 1.26354 10.2816 1.27173ZM10.3967 2.27095C10.3723 2.27947 10.3711 2.28076 10.3155 2.35762C10.2656 2.42661 10.2248 2.49568 10.1287 2.67402C9.89706 3.10371 9.77108 3.28339 9.53974 3.51402C9.32398 3.72912 9.13538 3.86167 8.74724 4.07102C8.51131 4.19827 8.45956 4.22915 8.36602 4.2985C8.31564 4.33584 8.30484 4.35058 8.30484 4.38202C8.30484 4.39251 8.3078 4.40759 8.31142 4.41552C8.32536 4.44612 8.47233 4.54397 8.64364 4.6367C8.87174 4.76018 8.93784 4.79699 9.01876 4.84558C9.36941 5.05617 9.65989 5.33291 9.88605 5.67186C9.95273 5.7718 10.0156 5.88013 10.1299 6.0924C10.2236 6.26625 10.2731 6.34919 10.3292 6.42618C10.3786 6.49395 10.3987 6.50673 10.4387 6.49596C10.4869 6.48298 10.5644 6.36742 10.7241 6.07042C10.7713 5.98264 10.8312 5.87428 10.8572 5.82962C11.0802 5.44687 11.3622 5.14671 11.7212 4.90993C11.8234 4.84255 11.8938 4.80181 12.1163 4.68134C12.3069 4.57811 12.3856 4.53158 12.4572 4.47984C12.5259 4.43019 12.5356 4.41809 12.5356 4.38202C12.5356 4.34596 12.5259 4.33386 12.4572 4.28421C12.3855 4.23239 12.3068 4.18589 12.1156 4.0824C12.0171 4.02905 11.8999 3.96401 11.8552 3.93786C11.5352 3.75051 11.2676 3.51463 11.0538 3.23122C10.944 3.08567 10.8698 2.96464 10.7241 2.69362C10.6097 2.48096 10.5541 2.38884 10.4951 2.31431C10.4587 2.26831 10.4346 2.25767 10.3967 2.27095ZM0.623558 2.76909C0.432324 2.80389 0.279236 2.94524 0.226143 3.13603C0.211728 3.18782 0.211728 3.33622 0.226143 3.38801C0.26998 3.54553 0.391847 3.67909 0.541232 3.73331C0.629528 3.76536 0.449202 3.76345 3.23404 3.76187L5.79324 3.76042L5.83804 3.74773C5.93425 3.72048 6.00757 3.6755 6.07824 3.60038C6.13087 3.54443 6.16564 3.48673 6.18933 3.41602C6.20674 3.36408 6.20716 3.36044 6.20716 3.26202C6.20716 3.16361 6.20674 3.15996 6.18933 3.10802C6.16564 3.03731 6.13087 2.97961 6.07824 2.92366C6.00757 2.84854 5.93425 2.80356 5.83804 2.77631L5.79324 2.76362L3.22844 2.7627C1.14236 2.76196 0.656161 2.76315 0.623558 2.76909ZM0.623558 6.01709C0.432324 6.05188 0.279236 6.19324 0.226143 6.38403C0.211728 6.43582 0.211728 6.58422 0.226143 6.63601C0.26998 6.79353 0.391847 6.92709 0.541232 6.98131C0.629528 7.01336 0.449202 7.01145 3.23404 7.00987L5.79324 7.00842L5.83804 6.99573C5.93425 6.96848 6.00757 6.9235 6.07824 6.84838C6.13087 6.79243 6.16564 6.73473 6.18933 6.66402C6.20674 6.61208 6.20716 6.60844 6.20716 6.51002C6.20716 6.41161 6.20674 6.40796 6.18933 6.35602C6.16564 6.28531 6.13087 6.22761 6.07824 6.17166C6.00757 6.09654 5.93425 6.05156 5.83804 6.02431L5.79324 6.01162L3.22844 6.0107C1.14236 6.00996 0.656161 6.01115 0.623558 6.01709ZM0.691643 9.26314C0.532995 9.28297 0.385317 9.38912 0.310882 9.53682C0.253908 9.64987 0.244438 9.79552 0.28599 9.91955C0.30638 9.9804 0.326472 10.0168 0.369497 10.071C0.434799 10.1532 0.51624 10.2084 0.619924 10.241L0.669243 10.2564H6.89924H13.1292L13.1796 10.2407C13.3435 10.1896 13.4746 10.0568 13.5207 9.89522C13.5431 9.817 13.5421 9.70057 13.5184 9.61612C13.4974 9.54094 13.4526 9.46699 13.3915 9.40661C13.3045 9.32063 13.215 9.27658 13.0986 9.26254C13.0387 9.25533 0.749435 9.25591 0.691643 9.26314ZM0.691643 12.5111C0.532995 12.531 0.385317 12.6371 0.310882 12.7848C0.253908 12.8979 0.244438 13.0435 0.28599 13.1676C0.30638 13.2284 0.326472 13.2648 0.369497 13.319C0.434799 13.4012 0.51624 13.4564 0.619924 13.489L0.669243 13.5044H6.89924H13.1292L13.1796 13.4887C13.3435 13.4376 13.4746 13.3048 13.5207 13.1432C13.5431 13.065 13.5421 12.9486 13.5184 12.8641C13.4974 12.7889 13.4526 12.715 13.3915 12.6546C13.3045 12.5686 13.215 12.5246 13.0986 12.5105C13.0387 12.5033 0.749435 12.5039 0.691643 12.5111Z"
                                fill="url(#paint0_linear_0_8287)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_0_8287"
                                    x1="3.5"
                                    y1="6.99976"
                                    x2="14"
                                    y2="13.9998"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stopColor="#E45EFF" />
                                    <stop offset="1" stopColor="#3C3FFF" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </span>
                    <span className={s.sectionTitle}>AI summary</span>
                </div>
                <div>
                    <div className={s.aiSummary}>{caseData.aiSummary}</div>
                    <div className={s.tags}>
                        {caseData.aiTags &&
                            caseData.aiTags.split(',').map((tag, index) => (
                                <Tag key={index} className={s.summaryTag}>
                                    {tag}
                                </Tag>
                            ))}
                    </div>
                </div>
            </div>

            {/* Medical History */}
            <div className={s.section}>
                <div className={s.sectionTitle}>
                    <div className={s.sectionTitleText}>Medical history</div>
                    {isAcceptMode && (
                        <div
                            className={s.sectionTitleButton}
                            onClick={handleOpenPatientUpdateDialog(PatientUpdateType.MedicalHistory)}
                        >
                            <img src={PencilIcon} /> Edit
                        </div>
                    )}
                </div>
                <div className={s.tagList}>
                    {caseData.historyMedical?.length ? (
                        caseData.historyMedical?.map((item, index) => (
                            <Tag key={index} className={s.tag}>
                                {item}
                            </Tag>
                        ))
                    ) : (
                        <div className="text-gray-900">No medical history provided</div>
                    )}
                </div>
            </div>

            {/* Current Medications */}
            <div className={s.section}>
                <div className={s.sectionTitle}>
                    <div className={s.sectionTitleText}>Current medications</div>
                    {isAcceptMode && (
                        <div
                            className={s.sectionTitleButton}
                            onClick={handleOpenPatientUpdateDialog(PatientUpdateType.CurrentMedications)}
                        >
                            <img src={PencilIcon} /> Edit
                        </div>
                    )}
                </div>

                <div className={s.tagList}>
                    {caseData.currentMedications?.length ? (
                        caseData.currentMedications?.map((item, index) => (
                            <Tag key={index} className={s.tag}>
                                {item}
                            </Tag>
                        ))
                    ) : (
                        <div className="text-gray-900">No medications listed</div>
                    )}
                </div>
            </div>

            {/* Allergies */}
            <div className={s.section}>
                <div className={s.sectionTitle}>
                    <div className={s.sectionTitleText}>Allergies</div>
                    {isAcceptMode && (
                        <div
                            className={s.sectionTitleButton}
                            onClick={handleOpenPatientUpdateDialog(PatientUpdateType.Allergies)}
                        >
                            <img src={PencilIcon} /> Edit
                        </div>
                    )}
                </div>

                <div className={s.tagList}>
                    {caseData.allergies?.length ? (
                        caseData.allergies?.map((item, index) => (
                            <Tag key={index} className={s.tag}>
                                {item}
                            </Tag>
                        ))
                    ) : (
                        <div className="text-gray-900">No allergies reported</div>
                    )}
                </div>
            </div>

            {/* Intake Questions */}
            <div className={s.intakeSection}>
                <div className={s.collapsibleHeader} onClick={() => setIntakeExpanded(!intakeExpanded)}>
                    <span className={classNames(s.sectionTitle, s.intakeSectionTitle)}>Intake questions</span>
                    {intakeExpanded ? <UpOutlined /> : <DownOutlined />}
                </div>

                {intakeExpanded && (
                    <div className={s.intakeContent}>
                        {caseData.intakeQuestions?.map((item, index) => {
                            if (!item.answer) return null;

                            const questionName = item.shortQuestionName || item.fullQuestionName || '';
                            const sensitiveType = shouldMaskData ? getSensitiveType(questionName) : null;

                            let answer;
                            try {
                                answer = JSON.parse(item.answer);
                            } catch {
                                answer = item.answer;
                            }

                            if (!answer || (Array.isArray(answer) && answer.length === 0)) return null;
                            if (!Array.isArray(answer)) answer = [answer];
                            answer = answer.filter((a: unknown) => a != null);
                            if (answer.length === 0) return null;

                            return (
                                <div className={s.intakeItem} key={index}>
                                    <div className={s.intakeLabel}>{item.shortQuestionName}</div>
                                    <div className={s.intakeValue}>
                                        {answer.length > 1
                                            ? answer.map((ans: unknown, idx: number) =>
                                                  renderAnswerItem(ans, idx, true, !!sensitiveType, sensitiveType),
                                              )
                                            : renderAnswerItem(answer[0], 0, false, !!sensitiveType, sensitiveType)}
                                    </div>
                                </div>
                            );
                        })}
                        {caseData.patientAdditionalComment && (
                            <div className={s.intakeItem} key="additional-comment">
                                <div className={s.intakeLabel}>Additional comment</div>
                                <div className={s.intakeValue}>
                                    {shouldMaskData
                                        ? maskAnswerText(caseData.patientAdditionalComment, null)
                                        : caseData.patientAdditionalComment}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className={s.section}>
                <div className={s.sectionTitle}>
                    <div className={s.sectionTitleText}>ID</div>
                    {isAcceptMode && (
                        <div className={s.sectionTitleButton} onClick={() => setIdUpdateDialogOpen(true)}>
                            <img src={PencilIcon} /> Edit
                        </div>
                    )}
                </div>
                <div className="flex flex-col gap-2">
                    <Tag className={`${s.tag} ${caseData.idVerified ? s.verifiedTag : s.unverifiedTag}`}>
                        {caseData.idVerified ? 'ID verified' : 'ID missing'}
                    </Tag>
                    <div className={s.idImages}>
                        {caseData.patientIdFrontUrl && (
                            <div className={s.idImageSection}>
                                <div className={s.idImageTitle}>Front of ID</div>
                                {isProviderEligible ? (
                                    <div className={s.idImageContainer}>
                                        <img src={caseData.patientIdFrontUrl} alt="Front of ID" className={s.idImage} />
                                        <div
                                            className={s.expandIcon}
                                            onClick={() => handleImageClick(caseData.patientIdFrontUrl!)}
                                        >
                                            <ExpandOutlined />
                                        </div>
                                    </div>
                                ) : (
                                    <div className={s.idImageContainer}>
                                        <img src={MaskedFrontImage} alt="Front of ID" className={s.idImage} />
                                    </div>
                                )}
                            </div>
                        )}

                        {caseData.patientIdBackUrl && (
                            <div className={s.idImageSection}>
                                <div className={s.idImageTitle}>Back of ID</div>
                                {isProviderEligible ? (
                                    <div className={s.idImageContainer}>
                                        <img src={caseData.patientIdBackUrl} alt="Back of ID" className={s.idImage} />
                                        <div
                                            className={s.expandIcon}
                                            onClick={() => handleImageClick(caseData.patientIdBackUrl!)}
                                        >
                                            <ExpandOutlined />
                                        </div>
                                    </div>
                                ) : (
                                    <div className={s.idImageContainer}>
                                        <img src={MaskedBackImage} alt="Back of ID" className={s.idImage} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <Modal
                    open={imageModalVisible}
                    onCancel={() => setImageModalVisible(false)}
                    footer={null}
                    width="80%"
                    centered
                >
                    <img
                        src={currentImage}
                        alt="ID Document"
                        style={{ width: '100%', height: 'auto', maxWidth: '80vw', maxHeight: '80vh' }}
                    />
                </Modal>
            </div>
        </>
    );

    const renderProspectInfo = () => {
        return (
            <ProsepectInfo
                textVisitItem={caseData}

                data={caseData.prospect}
                loading={false}
                useForModule
                isSimpleType
                smallSize
                onUpdate={() => {
                    refetchCaseData?.();
                }}
                isInTextVisitPop
                showBillingForVisit={showBillingForVisit}
                onChatWithPatient={onChat}
            />
        );
    };

    return (
        <div className={`${s.previewDetail} ${isAcceptMode ? s.acceptModeDetail : ''}`}>
            {showHeader && (
                <>
                    <div className={s.headerInfo}>
                        <div className={s.title}>
                            [{caseData.patientFirstName} {caseData.patientLastName}] {caseTitle}{' '}
                        </div>
                        {!isAcceptMode && (
                            <div className={s.submittedDate}>
                                Submitted on {dayjs(caseData.patientSubmitAt).format('MMM DD')}
                            </div>
                        )}
                    </div>

                    <div className={s.statusTags}>
                        {caseData.paymentStatus && (
                            <Tag className={s.paidTag}>
                                {caseData.paymentStatus === IPaymentStatus.SUCCEEDED && 'Paid'}
                                {caseData.paymentStatus === IPaymentStatus.REFUNDED && 'Refunded'}
                                {caseData.paymentStatus === IPaymentStatus.REFUNDED_FAILED && 'Refund failed'}
                                {caseData.paymentStatus === IPaymentStatus.REFUNDED_PROCESSING && 'Refund processing'}
                            </Tag>
                        )}
                        <Tag className={`${s.tag} ${caseData.idVerified ? s.verifiedTag : s.unverifiedTag}`}>
                            {caseData.idVerified ? 'ID verified' : 'ID missing'}
                        </Tag>
                        <Tag className={`${s.tag} ${caseData.pharmacySelected ? s.verifiedTag : s.unverifiedTag}`}>
                            {caseData.pharmacySelected ? 'Pharmacy selected' : 'Pharmacy missing'}
                        </Tag>
                    </div>
                </>
            )}
            {/* remove on 2026/05/25 */}
            {/* {!intakeFormCompleted && (
                <div className={s.intakeFormItem}>
                    <div className={s.intakeFormItemLabel}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M8.25803 3.09956C9.02264 1.74025 10.9797 1.74025 11.7443 3.09956L17.3246 13.0201C18.0746 14.3533 17.1111 16.0006 15.5815 16.0006H4.42088C2.89123 16.0006 1.9278 14.3533 2.67773 13.0201L8.25803 3.09956ZM11.0011 13.0007C11.0011 13.553 10.5534 14.0007 10.0011 14.0007C9.44881 14.0007 9.0011 13.553 9.0011 13.0007C9.0011 12.4484 9.44881 12.0007 10.0011 12.0007C10.5534 12.0007 11.0011 12.4484 11.0011 13.0007ZM10.0011 5.00073C9.44881 5.00073 9.0011 5.44845 9.0011 6.00073V9.00073C9.0011 9.55302 9.44881 10.0007 10.0011 10.0007C10.5534 10.0007 11.0011 9.55302 11.0011 9.00073V6.00073C11.0011 5.44845 10.5534 5.00073 10.0011 5.00073Z"
                                fill="#D97706"
                            />
                        </svg>
                        Intake form pending
                    </div>
                    <div className={s.intakeFormItemSend}>
                        <Button
                            className={s.intakeFormReminderBtn}
                            loading={sendingIntakeReminder}
                            onClick={handleSendIntakeFormReminder}
                            type="text"
                        >
                            <span>Send reminder</span>
                            <Tooltip
                                overlayClassName={s.tipCard}
                                overlayInnerStyle={{
                                    width: '330px',
                                    padding: '16px',
                                    borderRadius: '24px',
                                }}
                                color="#ffffff"
                                title={
                                    <div className={s.tooltipContent} style={{ color: '#333' }}>
                                        <div className={s.tooltipTitle}>📩 Intake form reminder message preview</div>
                                        <div className={s.tooltipMessage}>
                                            <p>Hi [Patient&apos;s Name],</p>
                                            <p>
                                                Just a quick reminder to complete your intake form to help us prepare
                                                for your visit. Please fill it out at your earliest convenience using
                                                the link below:
                                            </p>
                                            <p className={s.tooltipLink}>[Intake Form URL]</p>
                                            <p>
                                                If you have any questions, feel free to reach out. We&apos;re here to
                                                help!
                                            </p>
                                        </div>
                                    </div>
                                }
                                placement="top"
                            >
                                <svg width="16" height="16" viewBox="0 0 14 14" fill="none" className={s.questionIcon}>
                                    <path
                                        d="M4.48511 5C4.85118 4.22321 5.83895 3.66667 7.00004 3.66667C8.4728 3.66667 9.6667 4.5621 9.6667 5.66667C9.6667 6.59963 8.81496 7.38338 7.66285 7.6044C7.30125 7.67377 7.00004 7.96514 7.00004 8.33333M7 10.3333H7.00667M13 7C13 10.3137 10.3137 13 7 13C3.68629 13 1 10.3137 1 7C1 3.68629 3.68629 1 7 1C10.3137 1 13 3.68629 13 7Z"
                                        stroke="#6B7280"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Tooltip>
                        </Button>
                    </div>
                </div>
            )} */}

            {isAcceptMode && (
                <Button onClick={onChat} className="bg-white flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="16" viewBox="0 0 18 16" fill="none">
                        <path
                            d="M5.66667 7.99992H5.675M9 7.99992H9.00833M12.3333 7.99992H12.3417M16.5 7.99992C16.5 11.6818 13.1421 14.6666 9 14.6666C7.71728 14.6666 6.50976 14.3803 5.45389 13.8758L1.5 14.6666L2.66249 11.5666C1.9263 10.5352 1.5 9.31182 1.5 7.99992C1.5 4.31802 4.85786 1.33325 9 1.33325C13.1421 1.33325 16.5 4.31802 16.5 7.99992Z"
                            stroke="#6B7280"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    Chat with patient
                </Button>
            )}
            {!isFollowUpCase ? renderPatientInfo() : renderProspectInfo()}
            <PatientHealthInfoUpdateDialog
                conditionHealth={caseData?.conditionHealth}
                medicationHealth={caseData?.medicationHealth}
                allergyHealth={caseData?.allergyHealth}
                prospectId={caseData?.prospect?.id}
                open={patientUpdateDialog.open}
                type={patientUpdateDialog.type as PatientUpdateType}
                onCancel={() => setPatientUpdateDialog({ ...initPatientUpdateDialog })}
                onUpdated={() => {
                    refetchCaseData?.();
                }}
            />
            <IDUpdateDialog
                open={idUpdateDialogOpen}
                onCancel={() => setIdUpdateDialogOpen(false)}
                caseData={caseData}
                onUpdated={() => {
                    refetchCaseData?.();
                }}
            />
            {contextHolder}
        </div>
    );
};

export default PreviewDetail;
